// Injects the non-essential trackers (Google Tag Manager + HubSpot) ONLY once
// the visitor has granted consent. Renders nothing otherwise — this is what
// enforces the opt-in: no GTM (and therefore no GA4 / Facebook Pixel, which live
// inside the GTM container) and no HubSpot until "Accept" is clicked.
//
// GTM was previously injected by `gatsby-plugin-google-tagmanager` (main-thread)
// or by a Partytown <Script> in gatsby-shared.js when GATSBY_GTM_PARTYTOWN=true.
// Both are now consolidated here, behind consent. The Partytown perf toggle is
// preserved.

import React from "react"
import { Script, ScriptStrategy } from "gatsby"
import { useConsent } from "./consent-context"
import { CONSENT_GRANTED } from "./consent-cookie"

const GTM_ID = "GTM-T7SMK6H"
const HUBSPOT_SRC = "https://js.hs-scripts.com/14572677.js"
const partytownEnabled = process.env.GATSBY_GTM_PARTYTOWN === "true"

// Off-main-thread GTM via Partytown. The snippet points at a same-origin path
// (/__gtm/gtm.js) that netlify.toml reverse-proxies to Google, because a web
// worker cannot fetch googletagmanager.com cross-origin. Mirror of the former
// gatsby-shared.js implementation.
const PartytownGtm = () => (
  <Script
    id="gtm-partytown"
    strategy={ScriptStrategy.offMainThread}
    forward={["dataLayer.push"]}
    dangerouslySetInnerHTML={{
      __html: `
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ platform: "gatsby" });
        (function (w, d, s, l, i) {
          w[l] = w[l] || [];
          w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
          var f = d.getElementsByTagName(s)[0],
            j = d.createElement(s),
            dl = l != 'dataLayer' ? '&l=' + l : '';
          j.async = true;
          j.src = '/__gtm/gtm.js?id=' + i + dl;
          f.parentNode.insertBefore(j, f);
        })(window, document, 'script', 'dataLayer', '${GTM_ID}');
      `,
    }}
  />
)

// Classic main-thread GTM, loaded post-hydration (idle) since we can only inject
// it after the client-side consent check.
const StandardGtm = () => (
  <Script
    id="gtm"
    strategy={ScriptStrategy.idle}
    dangerouslySetInnerHTML={{
      __html: `
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ platform: "gatsby" });
        (function (w, d, s, l, i) {
          w[l] = w[l] || [];
          w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
          var f = d.getElementsByTagName(s)[0],
            j = d.createElement(s),
            dl = l != 'dataLayer' ? '&l=' + l : '';
          j.async = true;
          j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
          f.parentNode.insertBefore(j, f);
        })(window, document, 'script', 'dataLayer', '${GTM_ID}');
      `,
    }}
  />
)

const TrackerScripts = () => {
  const { consent } = useConsent()
  if (consent !== CONSENT_GRANTED) return null

  return (
    <>
      {partytownEnabled ? <PartytownGtm /> : <StandardGtm />}
      <Script
        id="hs-script-loader"
        src={HUBSPOT_SRC}
        strategy={ScriptStrategy.idle}
      />
    </>
  )
}

export default TrackerScripts
